// SPDX-License-Identifier: AGPL-3.0-or-later

import GameMode from '@app/features/accessibility/state/GameMode';
import type {Channel} from '@app/features/channel/models/Channel';
import ExperimentAssignments from '@app/features/experiment/state/ExperimentAssignments';
import {SETTINGS_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import ReadStates from '@app/features/read_state/state/ReadStates';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {useCameraMode} from '@app/features/voice_scene/components/useCameraMode';
import {VoiceSceneAmbienceControl} from '@app/features/voice_scene/components/VoiceSceneAmbienceControl';
import {VoiceSceneChatBox, VoiceSceneChatReply} from '@app/features/voice_scene/components/VoiceSceneChatBox';
import {VoiceSceneChatPanel} from '@app/features/voice_scene/components/VoiceSceneChatPanel';
import {VoiceSceneEmoteBar} from '@app/features/voice_scene/components/VoiceSceneEmoteBar';
import {VoiceSceneFootstepControl} from '@app/features/voice_scene/components/VoiceSceneFootstepControl';
import {VoiceSceneMouseSensitivityControl} from '@app/features/voice_scene/components/VoiceSceneMouseSensitivityControl';
import {VoiceSceneSlapControl} from '@app/features/voice_scene/components/VoiceSceneSlapControl';
import styles from '@app/features/voice_scene/components/VoiceSceneToolbar.module.css';
import {useRoomEmotes} from '@app/features/voice_scene/hooks/useRoomEmotes';
import type {VoiceSceneHandle} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import VoiceSceneSettings, {type VoiceSceneFrameRate} from '@app/features/voice_scene/state/VoiceSceneSettings';
import {effectiveRoomTheme} from '@app/features/voice_scene/utils/EffectiveRoomTheme';
import {
	effectiveQualityTier,
	type RoomQualitySetting,
	type RoomQualityTier,
} from '@app/features/voice_scene/utils/RoomQuality';
import {getWebglSupport} from '@app/features/voice_scene/utils/WebglSupport';
import {voiceRoomHasAmbience} from '@fluxer/constants/src/VoiceRoomSounds';
import {readVoiceRoomAssignment} from '@fluxer/schema/src/domains/experiment/ExperimentSchemas';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {
	ArrowLeftIcon,
	ChatCircleIcon,
	EyesIcon,
	GaugeIcon,
	HeadphonesIcon,
	HouseIcon,
	type Icon,
	SlidersHorizontalIcon,
	SparkleIcon,
	UserCircleIcon,
	VideoCameraIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type MouseEvent, useId} from 'react';

const SPATIAL_AUDIO_DESCRIPTOR = msg({
	message: '3D audio',
	comment:
		'Voice call 3D room: toggle label. On, each voice comes from where the person sits; off, everyone sounds centred.',
});
const TURN_OFF_SPATIAL_AUDIO_DESCRIPTOR = msg({
	message: 'Turn off 3D audio',
	comment: 'Voice call 3D room: tooltip on the 3D audio toggle while it is on.',
});
const TURN_ON_SPATIAL_AUDIO_DESCRIPTOR = msg({
	message: 'Turn on 3D audio',
	comment: 'Voice call 3D room: tooltip on the 3D audio toggle while it is off.',
});
const CAMERAS_DESCRIPTOR = msg({
	message: 'Cameras',
	comment:
		"Voice call 3D room: toggle label. On, a person's camera plays in their avatar circle in the room; off, only avatars. Remembered on this device.",
});
const HIDE_CAMERAS_DESCRIPTOR = msg({
	message: 'Hide cameras in the room',
	comment: 'Voice call 3D room: tooltip on the cameras toggle while it is on.',
});
const SHOW_CAMERAS_DESCRIPTOR = msg({
	message: 'Show cameras in the room',
	comment: 'Voice call 3D room: tooltip on the cameras toggle while it is off.',
});
const BACK_DESCRIPTOR = msg({
	message: 'Back',
	context: 'voice-scene-toolbar',
	comment: 'Voice call 3D room: button while the camera is close on the TV; returns to the whole room.',
});
const BACK_TOOLTIP_DESCRIPTOR = msg({
	message: 'Back to the room (Esc)',
	comment: 'Voice call 3D room: tooltip on the Back button while the camera is close on the TV.',
});
const CHAT_DESCRIPTOR = msg({
	message: 'Chat',
	context: 'voice-scene-toolbar',
	comment: "Voice call 3D room: toggle label. On, this voice channel's messages show in the room.",
});
const SHOW_CHAT_DESCRIPTOR = msg({
	message: 'Show the chat',
	comment: "Voice call 3D room: tooltip on the Chat toggle while it is off; opens this voice channel's messages.",
});
const HIDE_CHAT_DESCRIPTOR = msg({
	message: 'Hide the chat',
	comment: 'Voice call 3D room: tooltip on the Chat toggle while it is on.',
});
const RESET_VIEW_DESCRIPTOR = msg({
	message: 'Reset view',
	comment: 'Voice call 3D room: button that glides the camera back to the starting view of the whole room.',
});
const RESET_VIEW_TOOLTIP_DESCRIPTOR = msg({
	message: 'Back to the starting view (H)',
	comment: 'Voice call 3D room: tooltip on the Reset view button. H is the keyboard shortcut.',
});
const SHARE_GAZE_DESCRIPTOR = msg({
	message: 'Share gaze',
	comment:
		'Voice call 3D room: toggle label. On, your character turns its head toward where your pointer is in the room, for everyone. Remembered on this device.',
});
const STOP_SHARING_GAZE_DESCRIPTOR = msg({
	message: 'Stop showing others where I look',
	comment: 'Voice call 3D room: tooltip on the Share gaze toggle while it is on.',
});
const START_SHARING_GAZE_DESCRIPTOR = msg({
	message: 'Show others where I look',
	comment: 'Voice call 3D room: tooltip on the Share gaze toggle while it is off.',
});
const CLASSIC_VIEW_DESCRIPTOR = msg({
	message: 'Classic view',
	comment:
		'Voice call 3D room: toggle label. On, people show as round profile pictures instead of 3D characters (only on this device). Remembered on this device.',
});
const SHOW_CHARACTERS_DESCRIPTOR = msg({
	message: 'Show characters',
	comment: 'Voice call 3D room: tooltip on the Classic view toggle while it is on.',
});
const SHOW_CIRCLES_DESCRIPTOR = msg({
	message: 'Show profile pictures instead of characters',
	comment: 'Voice call 3D room: tooltip on the Classic view toggle while it is off.',
});

const FRAME_RATE_DESCRIPTOR = msg({
	message: 'Frame rate',
	comment: 'Voice call 3D room: label and accessible name of the picker for how many frames per second the room draws.',
});
const FRAME_RATE_AUTO_DESCRIPTOR = msg({
	message: 'Auto',
	context: 'voice-scene-frame-rate',
	comment: 'Voice call 3D room: frame rate choice; the room picks its own frame rate for this device.',
});
const FRAME_RATE_30_DESCRIPTOR = msg({
	message: '30 FPS',
	comment: 'Voice call 3D room: frame rate choice, 30 frames per second.',
});
const FRAME_RATE_60_DESCRIPTOR = msg({
	message: '60 FPS',
	comment: 'Voice call 3D room: frame rate choice, 60 frames per second.',
});

const FRAME_RATE_60_HINT_DESCRIPTOR = msg({
	message: 'May delay messages on slower devices; Auto is recommended',
	comment: 'Voice call 3D room: tooltip on the 60 FPS frame rate choice.',
});

const QUALITY_DESCRIPTOR = msg({
	message: 'Quality',
	comment:
		'Voice call 3D room: label and accessible name of the picker for how much detail and effect (dust, glow, sharpness) the room draws on this device.',
});
const QUALITY_LITE_MODE_DESCRIPTOR = msg({
	message: 'Performance mode: 30 fps',
	comment:
		'Voice call 3D room: note beside the quality picker while Performance mode draws the room on low at 30 frames a second; the chosen quality comes back when Performance mode ends.',
});
const QUALITY_AUTO_DESCRIPTOR = msg({
	message: 'Auto ({tier})',
	context: 'voice-scene-quality',
	comment:
		'Voice call 3D room: quality choice; the room picks the quality for this device. {tier} is the quality it picked: Low, Medium or High.',
});
const QUALITY_LOW_DESCRIPTOR = msg({
	message: 'Low',
	context: 'voice-scene-quality',
	comment:
		'Voice call 3D room: quality choice for slower devices; no dust or glow, lighter textures, 30 frames a second.',
});
const QUALITY_MEDIUM_DESCRIPTOR = msg({
	message: 'Medium',
	context: 'voice-scene-quality',
	comment: 'Voice call 3D room: quality choice for average devices; dust and glow in the room.',
});
const QUALITY_HIGH_DESCRIPTOR = msg({
	message: 'High',
	context: 'voice-scene-quality',
	comment: 'Voice call 3D room: quality choice for fast devices; sharpest picture, dust, glow and a soft bloom.',
});
const QUALITY_LOW_HINT_DESCRIPTOR = msg({
	message: 'Lighter textures from the next time the room opens',
	comment: 'Voice call 3D room: tooltip on the Low quality choice; the rest of Low applies at once.',
});
const QUALITY_TIER_LABELS: Record<RoomQualityTier, MessageDescriptor> = {
	low: QUALITY_LOW_DESCRIPTOR,
	medium: QUALITY_MEDIUM_DESCRIPTOR,
	high: QUALITY_HIGH_DESCRIPTOR,
};
const QUALITY_CHOICES: ReadonlyArray<RoomQualitySetting> = ['auto', 'low', 'medium', 'high'];

const FRAME_RATE_CHOICES: ReadonlyArray<{value: VoiceSceneFrameRate; label: MessageDescriptor}> = [
	{value: 'auto', label: FRAME_RATE_AUTO_DESCRIPTOR},
	{value: 30, label: FRAME_RATE_30_DESCRIPTOR},
	{value: 60, label: FRAME_RATE_60_DESCRIPTOR},
];

function ToolbarButton({
	icon: IconComponent,
	label,
	tooltip,
	pressed,
	expanded,
	controls,
	iconOnly = false,
	mark = false,
	onClick,
}: {
	icon: Icon;
	label: MessageDescriptor;
	tooltip: MessageDescriptor;
	pressed?: boolean;
	expanded?: boolean;
	controls?: string;
	iconOnly?: boolean;
	mark?: boolean;
	onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
	const {i18n} = useLingui();
	const on = (expanded ?? pressed) !== false;
	return (
		<Tooltip
			text={i18n._(tooltip)}
			position={iconOnly ? 'top' : 'left'}
			data-flx="voice-scene.voice-scene-toolbar.toolbar-button.tooltip"
		>
			<FocusRing offset={-2} data-flx="voice-scene.voice-scene-toolbar.toolbar-button.focus-ring">
				<button
					type="button"
					className={clsx(styles.toggle, iconOnly && styles.iconOnly, on && styles.toggleOn)}
					aria-pressed={pressed}
					aria-expanded={expanded}
					aria-controls={controls}
					onClick={onClick}
					data-flx="voice-scene.voice-scene-toolbar.toolbar-button.toggle"
				>
					<IconComponent
						weight={on ? 'fill' : 'regular'}
						className={styles.icon}
						data-flx="voice-scene.voice-scene-toolbar.toolbar-button.icon"
					/>
					<span
						className={iconOnly ? styles.srOnly : styles.label}
						data-flx="voice-scene.voice-scene-toolbar.toolbar-button.label"
					>
						{i18n._(label)}
					</span>
					{mark ? <span className={styles.mark} data-flx="voice-scene.voice-scene-toolbar.chat-unread" /> : null}
				</button>
			</FocusRing>
		</Tooltip>
	);
}

// Alone in its own observer: a new unread message redraws this button, not the toolbar.
const ChatButton = observer(function ChatButton({channel, open}: {channel: Channel; open: boolean}) {
	return (
		<ToolbarButton
			icon={ChatCircleIcon}
			label={CHAT_DESCRIPTOR}
			tooltip={open ? HIDE_CHAT_DESCRIPTOR : SHOW_CHAT_DESCRIPTOR}
			pressed={open}
			iconOnly
			mark={!open && ReadStates.hasUnread(channel.id)}
			onClick={() => VoiceSceneSettings.setChatOpen(!open)}
		/>
	);
});

const FrameRatePicker = observer(function FrameRatePicker() {
	const {i18n} = useLingui();
	const label = i18n._(FRAME_RATE_DESCRIPTOR);
	return (
		<div className={styles.frameRate} data-flx="voice-scene.voice-scene-toolbar.frame-rate-picker.frame-rate">
			<GaugeIcon className={styles.icon} data-flx="voice-scene.voice-scene-toolbar.frame-rate-picker.icon" />
			<span className={styles.label} data-flx="voice-scene.voice-scene-toolbar.frame-rate-picker.label">
				{label}
			</span>
			<div className={styles.frameRateSelect} data-flx="voice-scene.voice-scene-toolbar.frame-rate-picker.select-wrap">
				<Combobox<VoiceSceneFrameRate>
					aria-label={label}
					value={VoiceSceneSettings.frameRate}
					options={FRAME_RATE_CHOICES.map((choice) => ({value: choice.value, label: i18n._(choice.label)}))}
					onChange={(value) => VoiceSceneSettings.setFrameRate(value)}
					renderOption={(option) =>
						option.value === 60 ? (
							<Tooltip
								text={i18n._(FRAME_RATE_60_HINT_DESCRIPTOR)}
								position="left"
								data-flx="voice-scene.voice-scene-toolbar.frame-rate-picker.hint"
							>
								<span data-flx="voice-scene.voice-scene-toolbar.frame-rate-picker.option">{option.label}</span>
							</Tooltip>
						) : (
							option.label
						)
					}
					isSearchable={false}
					isClearable={false}
					menuPlacement="top"
					density="compact"
					data-flx="voice-scene.voice-scene-toolbar.frame-rate-picker.select"
				/>
			</div>
		</div>
	);
});

const QualityPicker = observer(function QualityPicker() {
	const {i18n} = useLingui();
	const label = i18n._(QUALITY_DESCRIPTOR);
	const autoTier = effectiveQualityTier(
		{quality: 'auto', autoQualityCap: VoiceSceneSettings.autoQualityCap},
		getWebglSupport(),
	);
	const choiceLabel = (choice: RoomQualitySetting): string =>
		choice === 'auto'
			? i18n._(QUALITY_AUTO_DESCRIPTOR, {tier: i18n._(QUALITY_TIER_LABELS[autoTier])})
			: i18n._(QUALITY_TIER_LABELS[choice]);
	return (
		<div className={styles.frameRate} data-flx="voice-scene.voice-scene-toolbar.quality-picker.quality">
			<SparkleIcon className={styles.icon} data-flx="voice-scene.voice-scene-toolbar.quality-picker.icon" />
			<span className={styles.label} data-flx="voice-scene.voice-scene-toolbar.quality-picker.label">
				{label}
			</span>
			{GameMode.lightMode ? (
				<span className={styles.liteHint} data-flx="voice-scene.voice-scene-toolbar.quality-picker.lite-hint">
					{i18n._(QUALITY_LITE_MODE_DESCRIPTOR)}
				</span>
			) : null}
			<div className={styles.qualitySelect} data-flx="voice-scene.voice-scene-toolbar.quality-picker.select-wrap">
				<Combobox<RoomQualitySetting>
					aria-label={label}
					value={VoiceSceneSettings.quality}
					options={QUALITY_CHOICES.map((choice) => ({value: choice, label: choiceLabel(choice)}))}
					onChange={(value) => VoiceSceneSettings.setQuality(value)}
					renderOption={(option) =>
						option.value === 'low' ? (
							<Tooltip
								text={i18n._(QUALITY_LOW_HINT_DESCRIPTOR)}
								position="left"
								data-flx="voice-scene.voice-scene-toolbar.quality-picker.hint"
							>
								<span data-flx="voice-scene.voice-scene-toolbar.quality-picker.option">{option.label}</span>
							</Tooltip>
						) : (
							option.label
						)
					}
					isSearchable={false}
					isClearable={false}
					menuPlacement="top"
					density="compact"
					data-flx="voice-scene.voice-scene-toolbar.quality-picker.select"
				/>
			</div>
		</div>
	);
});

// Room-only controls; the flat/3D switch lives in the call header (VoiceSceneToggleButton). "Back" while the camera
// is close on the TV; the seat view has its own "Back to room" in the seat HUD. Closed, the card keeps reactions, the
// message box and the way back; Settings opens the room switches and the pickers above them, and the card grows
// upwards. Movements, the outfit and the eyes live in the action dock (VoiceSceneActionDock). Classic view keeps only its
// own switch back among the switches. The chat opens above them; classic view has
// no bubbles and no message box until then.
export const VoiceSceneToolbar = observer(function VoiceSceneToolbar({
	handle,
	channel,
	standing = false,
	canvas = null,
}: {
	handle: VoiceSceneHandle | null;
	channel: Channel;
	standing?: boolean;
	canvas?: HTMLCanvasElement | null;
}) {
	const mode = useCameraMode(handle);
	const settingsId = useId();
	const open = VoiceSceneSettings.toolbarExpanded;
	const chatOpen = VoiceSceneSettings.chatOpen;
	const spatialAudio = VoiceSceneSettings.spatialAudio;
	const showCameras = VoiceSceneSettings.showRoomCameras;
	const classicView = VoiceSceneSettings.classicView;
	const shareGaze = VoiceSceneSettings.shareHeadDirection;
	const roomAssignment = readVoiceRoomAssignment(ExperimentAssignments.response);
	const {cameras, avatars, walk} = roomAssignment;
	const theme = effectiveRoomTheme(channel.roomTheme, roomAssignment.default_room_theme, channel.type);
	const characters = avatars && !classicView;
	const canType = characters || chatOpen;
	useRoomEmotes({canvas, standing, enabled: characters});
	// The settings come after their button for focus order; the reversed column draws them above it.
	return (
		<div
			className={styles.toolbar}
			data-chat-open={chatOpen ? '' : undefined}
			data-flx="voice-scene.voice-scene-toolbar.toolbar"
		>
			<div className={styles.dock} data-flx="voice-scene.voice-scene-toolbar.dock">
				{characters ? (
					<VoiceSceneEmoteBar channel={channel} data-flx="voice-scene.voice-scene-toolbar.voice-scene-emote-bar" />
				) : null}
				{canType ? (
					<VoiceSceneChatReply
						channel={channel}
						className={styles.reply}
						data-flx="voice-scene.voice-scene-toolbar.voice-scene-chat-reply"
					/>
				) : null}
				<div className={styles.bottomRow} data-flx="voice-scene.voice-scene-toolbar.bottom-row">
					{canType ? (
						<VoiceSceneChatBox channel={channel} data-flx="voice-scene.voice-scene-toolbar.voice-scene-chat-box" />
					) : null}
					{handle?.goHome && mode === 'orbit' ? (
						<ToolbarButton
							icon={HouseIcon}
							label={RESET_VIEW_DESCRIPTOR}
							tooltip={RESET_VIEW_TOOLTIP_DESCRIPTOR}
							iconOnly
							onClick={() => handle.goHome?.()}
							data-flx="voice-scene.voice-scene-toolbar.toolbar-button.go-home"
						/>
					) : null}
					{handle && mode === 'focus' ? (
						<ToolbarButton
							icon={ArrowLeftIcon}
							label={BACK_DESCRIPTOR}
							tooltip={BACK_TOOLTIP_DESCRIPTOR}
							iconOnly
							onClick={() => handle.exitToOrbit()}
							data-flx="voice-scene.voice-scene-toolbar.toolbar-button.exit-to-orbit"
						/>
					) : null}
					<ChatButton channel={channel} open={chatOpen} data-flx="voice-scene.voice-scene-toolbar.chat-button" />
					<ToolbarButton
						icon={SlidersHorizontalIcon}
						label={SETTINGS_DESCRIPTOR}
						tooltip={SETTINGS_DESCRIPTOR}
						expanded={open}
						controls={settingsId}
						iconOnly
						onClick={() => VoiceSceneSettings.setToolbarExpanded(!open)}
						data-flx="voice-scene.voice-scene-toolbar.toolbar-button.room-controls"
					/>
				</div>
			</div>
			<div
				id={settingsId}
				className={styles.section}
				hidden={!open}
				data-flx="voice-scene.voice-scene-toolbar.settings"
			>
				<div className={styles.toggles} data-flx="voice-scene.voice-scene-toolbar.row">
					<ToolbarButton
						icon={HeadphonesIcon}
						label={SPATIAL_AUDIO_DESCRIPTOR}
						tooltip={spatialAudio ? TURN_OFF_SPATIAL_AUDIO_DESCRIPTOR : TURN_ON_SPATIAL_AUDIO_DESCRIPTOR}
						pressed={spatialAudio}
						onClick={() => VoiceSceneSettings.setSpatialAudio(!spatialAudio)}
						data-flx="voice-scene.voice-scene-toolbar.toolbar-button.set-spatial-audio"
					/>
					{cameras ? (
						<ToolbarButton
							icon={VideoCameraIcon}
							label={CAMERAS_DESCRIPTOR}
							tooltip={showCameras ? HIDE_CAMERAS_DESCRIPTOR : SHOW_CAMERAS_DESCRIPTOR}
							pressed={showCameras}
							onClick={() => VoiceSceneSettings.setShowRoomCameras(!showCameras)}
							data-flx="voice-scene.voice-scene-toolbar.toolbar-button.set-show-room-cameras"
						/>
					) : null}
					{characters ? (
						<ToolbarButton
							icon={EyesIcon}
							label={SHARE_GAZE_DESCRIPTOR}
							tooltip={shareGaze ? STOP_SHARING_GAZE_DESCRIPTOR : START_SHARING_GAZE_DESCRIPTOR}
							pressed={shareGaze}
							onClick={() => VoiceSceneSettings.setShareHeadDirection(!shareGaze)}
							data-flx="voice-scene.voice-scene-toolbar.toolbar-button.set-share-head-direction"
						/>
					) : null}
					{avatars ? (
						<ToolbarButton
							icon={UserCircleIcon}
							label={CLASSIC_VIEW_DESCRIPTOR}
							tooltip={classicView ? SHOW_CHARACTERS_DESCRIPTOR : SHOW_CIRCLES_DESCRIPTOR}
							pressed={classicView}
							onClick={() => VoiceSceneSettings.setClassicView(!classicView)}
							data-flx="voice-scene.voice-scene-toolbar.toolbar-button.set-classic-view"
						/>
					) : null}
				</div>
				<FrameRatePicker data-flx="voice-scene.voice-scene-toolbar.frame-rate-picker" />
				<QualityPicker data-flx="voice-scene.voice-scene-toolbar.quality-picker" />
				{theme && voiceRoomHasAmbience(theme) ? (
					<VoiceSceneAmbienceControl data-flx="voice-scene.voice-scene-toolbar.ambience-control" />
				) : null}
				{characters && walk ? (
					<>
						<VoiceSceneFootstepControl data-flx="voice-scene.voice-scene-toolbar.footstep-control" />
						<VoiceSceneMouseSensitivityControl data-flx="voice-scene.voice-scene-toolbar.mouse-sensitivity-control" />
					</>
				) : null}
				{characters && handle?.canEmote?.('slapped') ? (
					<VoiceSceneSlapControl data-flx="voice-scene.voice-scene-toolbar.slap-control" />
				) : null}
			</div>
			{chatOpen ? (
				<VoiceSceneChatPanel channel={channel} data-flx="voice-scene.voice-scene-toolbar.voice-scene-chat-panel" />
			) : null}
		</div>
	);
});
